import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronRight,
  Code,
  Copy,
  Filter,
  Info,
  Pause,
  Phone,
  Play,
  Radio,
  RefreshCw,
  Search,
  Trash2,
  Webhook,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, TENANT_ID } from '../utils/api';

export interface WebhookEvent {
  id: string;
  timestamp: string;
  source: 'meta' | 'generic-channel' | string;
  object?: string;
  entryCount: number;
  field?: string;
  phoneNumberId?: string;
  displayPhoneNumber?: string;
  tenantId?: string;
  summary: string;
  rawPayload: any;
}

const FIELD_OPTIONS = [
  { value: 'ALL', label: 'Todos los eventos' },
  { value: 'messages', label: 'messages (Mensajes y chats)' },
  { value: 'statuses', label: 'statuses (Entregado / Leído)' },
  { value: 'message_template_status_update', label: 'message_template (Plantillas)' },
  { value: 'account_update', label: 'account_update (Estado de cuenta)' },
  { value: 'phone_number_name_update', label: 'phone_number_name (Verificación nombre)' },
  { value: 'phone_number_quality_update', label: 'phone_number_quality (Calidad)' },
];

function formatRelativeTime(isoString: string): string {
  try {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 5) return 'hace un momento';
    if (diffSec < 60) return `hace ${diffSec}s`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `hace ${diffMin}m`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `hace ${diffHours}h`;
    return new Date(isoString).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  } catch {
    return isoString;
  }
}

function getFieldBadge(field?: string) {
  const f = (field || '').toLowerCase();
  if (f === 'messages') {
    return {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      label: 'messages',
    };
  }
  if (f === 'statuses') {
    return {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      label: 'statuses',
    };
  }
  if (f === 'message_template_status_update') {
    return {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
      label: 'template',
    };
  }
  if (f === 'account_update') {
    return {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      label: 'account',
    };
  }
  if (f.includes('phone_number')) {
    return {
      bg: 'bg-cyan-50',
      text: 'text-cyan-700',
      border: 'border-cyan-200',
      label: 'phone',
    };
  }
  return {
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    label: field || 'evento',
  };
}

export default function LiveWebhookInspector() {
  const { token } = useAuth();
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(true);
  const [selectedField, setSelectedField] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const webhookPublicUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/webhooks/meta`;

  const fetchWebhooks = async (showLoadingIndicator = true) => {
    if (!token) return;
    if (showLoadingIndicator && events.length === 0) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (selectedField !== 'ALL') {
        params.append('field', selectedField);
      }

      const res = await fetch(`${API_BASE_URL}/admin/webhooks/recent?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });

      if (!res.ok) {
        throw new Error(`Error ${res.status}: Fallo al consultar webhooks recientes`);
      }

      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setEvents(json.data);
        setError(null);
      }
    } catch (err: unknown) {
      if (showLoadingIndicator) {
        setError(err instanceof Error ? err.message : 'Error desconocido de red');
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void fetchWebhooks(true);

    if (!isStreaming) return;

    const interval = setInterval(() => {
      void fetchWebhooks(false);
    }, 2500);

    return () => clearInterval(interval);
  }, [token, isStreaming, selectedField]);

  const handleClearBuffer = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/webhooks/recent`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });

      if (res.ok) {
        setEvents([]);
        setSelectedEvent(null);
      }
    } catch (err) {
      console.error('Error al limpiar buffer', err);
    }
  };

  const handleCopyJson = (id: string, text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleCopyWebhookUrl = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(webhookPublicUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const q = searchQuery.toLowerCase().trim();
    return events.filter((e) => {
      if (e.summary.toLowerCase().includes(q)) return true;
      if (e.id.toLowerCase().includes(q)) return true;
      if (e.phoneNumberId && e.phoneNumberId.toLowerCase().includes(q)) return true;
      if (e.displayPhoneNumber && e.displayPhoneNumber.toLowerCase().includes(q)) return true;
      if (e.field && e.field.toLowerCase().includes(q)) return true;
      if (e.tenantId && e.tenantId.toLowerCase().includes(q)) return true;
      try {
        return JSON.stringify(e.rawPayload).toLowerCase().includes(q);
      } catch {
        return false;
      }
    });
  }, [events, searchQuery]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Cabecera Principal */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-200/60 flex items-center justify-center text-indigo-600 shadow-xs">
              <Webhook className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                Live Webhook Inspector
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                  Buffer 100
                </span>
              </h1>
              <p className="text-xs text-gray-500">
                Monitoreo en tiempo real de los payloads JSON crudos enviados por Meta WhatsApp Cloud API a la plataforma.
              </p>
            </div>
          </div>
        </div>

        {/* Indicador de estado y controles globales */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Indicador de streaming */}
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
              isStreaming
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-slate-100 text-slate-600 border-slate-200'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isStreaming ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
              }`}
            />
            {isStreaming ? 'En vivo (2.5s)' : 'Pausado'}
          </div>

          {/* Botón Pausar / Reanudar */}
          <button
            type="button"
            onClick={() => setIsStreaming((prev) => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-xs transition-colors"
            title={isStreaming ? 'Pausar actualización automática' : 'Reanudar actualización en vivo'}
          >
            {isStreaming ? <Pause className="w-3.5 h-3.5 text-amber-600" /> : <Play className="w-3.5 h-3.5 text-emerald-600" />}
            {isStreaming ? 'Pausar' : 'Reanudar'}
          </button>

          {/* Botón Refrescar manual */}
          <button
            type="button"
            onClick={() => void fetchWebhooks(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-xs transition-colors"
            title="Consultar webhooks ahora"
          >
            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
            Refrescar
          </button>

          {/* Botón Limpiar lista */}
          <button
            type="button"
            onClick={() => void handleClearBuffer()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-rose-200 bg-rose-50/60 hover:bg-rose-100/80 text-rose-700 transition-colors"
            title="Vaciar buffer de eventos en memoria"
          >
            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
            Limpiar lista
          </button>
        </div>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
          {/* Selector de campo */}
          <div className="w-full md:w-72">
            <label htmlFor="field-filter-select" className="sr-only">Filtrar por tipo de evento</label>
            <div className="relative">
              <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                id="field-filter-select"
                aria-label="Filtrar por tipo de evento"
                value={selectedField}
                onChange={(e) => setSelectedField(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-xs font-medium bg-gray-50/70 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
              >
                {FIELD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Campo de búsqueda */}
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar por teléfono, ID de mensaje, texto o payload..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50/70 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Conteo de resultados */}
          <div className="text-xs text-gray-500 shrink-0 self-center px-1">
            Mostrando <span className="font-bold text-gray-800">{filteredEvents.length}</span> de{' '}
            <span className="font-bold text-gray-800">{events.length}</span> webhooks
          </div>
        </div>

        {/* Tip para configuración de Meta */}
        <div className="flex items-center justify-between text-[11px] text-gray-500 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200/60">
          <div className="flex items-center gap-1.5 truncate">
            <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <span className="truncate">
              Webhook URL configurada en Meta App Dashboard:{' '}
              <code className="font-mono text-indigo-700 bg-indigo-50 px-1 py-0.5 rounded border border-indigo-200/60">
                {webhookPublicUrl}
              </code>
            </span>
          </div>
          <button
            type="button"
            onClick={handleCopyWebhookUrl}
            className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-semibold shrink-0 ml-2"
          >
            {copiedUrl ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
            {copiedUrl ? 'Copiada' : 'Copiar URL'}
          </button>
        </div>
      </div>

      {/* Contenido Principal: Grid de lista y detalle */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Columna Izquierda: Lista de Webhooks */}
        <div className={`space-y-3 ${selectedEvent ? 'lg:col-span-5' : 'lg:col-span-12'}`}>
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs flex items-center justify-between">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => void fetchWebhooks(true)}
                className="font-bold underline hover:no-underline ml-2"
              >
                Reintentar
              </button>
            </div>
          )}

          {loading && events.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-200 shadow-xs space-y-3">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent" />
              <p className="text-sm font-semibold text-gray-700">Cargando webhooks recientes...</p>
              <p className="text-xs text-gray-500">Conectando con el buffer de depuración de Meta.</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-200 shadow-xs space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
                <Radio className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-gray-800">
                {events.length === 0 ? 'Sin webhooks recibidos aún' : 'No hay webhooks que coincidan con el filtro'}
              </h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto">
                {events.length === 0
                  ? 'Cuando Meta envíe un evento HTTP POST a la URL de webhook, aparecerá aquí inmediatamente con su JSON crudo completo.'
                  : 'Intenta cambiar el campo seleccionado o borrar la búsqueda de texto.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {filteredEvents.map((event) => {
                const badge = getFieldBadge(event.field);
                const isSelected = selectedEvent?.id === event.id;

                return (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-indigo-50/50 border-indigo-400 ring-2 ring-indigo-500/10 shadow-xs'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-xs'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1.5 min-w-0 flex-1">
                        {/* Fila de badges y timestamps */}
                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                          <span
                            className={`px-2 py-0.5 rounded-md font-bold text-[11px] border ${badge.bg} ${badge.text} ${badge.border}`}
                          >
                            {badge.label}
                          </span>

                          {event.phoneNumberId && (
                            <span className="font-mono text-[11px] font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                              ID: {event.phoneNumberId}
                            </span>
                          )}

                          {event.displayPhoneNumber && (
                            <span className="text-[11px] text-gray-600 flex items-center gap-1 font-mono">
                              <Phone className="w-3 h-3 text-gray-400" />
                              {event.displayPhoneNumber}
                            </span>
                          )}

                          <span className="text-[11px] text-gray-400 ml-auto" title={event.timestamp}>
                            {formatRelativeTime(event.timestamp)}
                          </span>
                        </div>

                        {/* Resumen del evento */}
                        <p className="text-xs font-semibold text-gray-900 truncate">
                          {event.summary}
                        </p>

                        {/* Metadatos secundarios */}
                        <div className="flex items-center gap-3 text-[11px] text-gray-500">
                          <span className="font-mono text-[10px] text-gray-400">
                            {event.id.slice(0, 8)}...
                          </span>
                          {event.tenantId && (
                            <span className="text-indigo-600 font-medium">
                              Tenant: {event.tenantId}
                            </span>
                          )}
                          <span>
                            {event.entryCount} {event.entryCount === 1 ? 'entrada' : 'entradas'}
                          </span>
                        </div>
                      </div>

                      {/* Icono de flecha o expandir */}
                      <ChevronRight
                        className={`w-4 h-4 shrink-0 transition-transform ${
                          isSelected ? 'text-indigo-600 translate-x-0.5' : 'text-gray-300'
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Columna Derecha: Detalle / Visualizador JSON crudo */}
        {selectedEvent && (
          <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden sticky top-6">
            {/* Header del Panel de Detalle */}
            <div className="p-4 border-b border-gray-200/80 bg-gray-50/50 flex items-center justify-between gap-3">
              <div className="space-y-0.5 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-md font-bold text-[11px] border ${
                      getFieldBadge(selectedEvent.field).bg
                    } ${getFieldBadge(selectedEvent.field).text} ${
                      getFieldBadge(selectedEvent.field).border
                    }`}
                  >
                    {selectedEvent.field || selectedEvent.object}
                  </span>
                  <span className="text-xs font-mono text-gray-500">
                    ID: {selectedEvent.id}
                  </span>
                </div>
                <h3 className="text-xs font-bold text-gray-900 truncate">
                  {selectedEvent.summary}
                </h3>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    handleCopyJson(
                      selectedEvent.id,
                      JSON.stringify(selectedEvent.rawPayload, null, 2)
                    )
                  }
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-xs transition-colors"
                >
                  {copiedId === selectedEvent.id ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copiedId === selectedEvent.id ? 'Copiado' : 'Copiar JSON'}
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedEvent(null)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Cerrar panel de detalle"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Metadatos en píldoras */}
            <div className="px-4 py-3 bg-slate-50/70 border-b border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="block text-[10px] font-bold text-gray-400 uppercase">Timestamp</span>
                <span className="font-semibold text-gray-700 font-mono text-[11px]">
                  {new Date(selectedEvent.timestamp).toLocaleTimeString('es-ES')}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-gray-400 uppercase">Origen</span>
                <span className="font-semibold text-gray-700">
                  {selectedEvent.source}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-gray-400 uppercase">Phone Number ID</span>
                <span className="font-semibold text-gray-700 font-mono text-[11px] truncate block">
                  {selectedEvent.phoneNumberId || '—'}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-gray-400 uppercase">Tenant</span>
                <span className="font-semibold text-gray-700 truncate block">
                  {selectedEvent.tenantId || 'Global / Sin asignar'}
                </span>
              </div>
            </div>

            {/* Visualizador de JSON Crudo */}
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-gray-700 flex items-center gap-1.5">
                  <Code className="w-3.5 h-3.5 text-indigo-500" />
                  Payload JSON Crudo (Meta Webhook)
                </span>
                <span className="text-[11px] text-gray-400">
                  {(JSON.stringify(selectedEvent.rawPayload).length / 1024).toFixed(1)} KB
                </span>
              </div>

              <div className="relative group">
                <pre
                  tabIndex={0}
                  aria-label="Payload JSON Crudo"
                  className="p-4 rounded-xl bg-slate-900 text-slate-100 font-mono text-xs leading-relaxed overflow-x-auto max-h-[500px] border border-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <code>{JSON.stringify(selectedEvent.rawPayload, null, 2)}</code>
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
